/**
 * AuthenticatorCodeEntry — the offline-fallback code form (Q-D-TOTP-VERIFY, 2026-08-29).
 *
 * Extracted from `<AuthenticatorChallengeFlow>` rather than inlined: adding code entry there took
 * that component to 6 conditional renders and 4 state areas, tripping
 * `web/render-method-complexity` at ERROR. GEN0-STRUCTURAL-ELIMINATION forbids suppressing a
 * structural rule, so the answer is decomposition — and it is the better shape regardless, because
 * the code form owns state (`code`, `error`, `submitting`) that the challenge screen has no
 * business holding.
 *
 * ## Why the user can read a code at all when they are locked out
 *
 * The secret is enrolled into a standards-compliant authenticator app via the `otpauth://` QR, so
 * the code is readable from that app even when PalmPuffin itself cannot be opened. That is what
 * makes this a real escape from `Q-D-PALM-CIRCULARITY` rather than a second door into the same room.
 *
 * @module @puffin-core/login-shell/components/AuthenticatorCodeEntry
 */

import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
// The NARROW subpath: login-shell's suites double the mfa-ui barrel to stub <MfaVerification>.
import { MfaCodeInput } from '@puffin-core/mfa-ui/code-input';
import { useLoginTranslate } from '../hooks/useLoginTranslate.js';

/** The input's id; its refusal line is `${CODE_INPUT_ID}-error`. */
const CODE_INPUT_ID = 'authenticator-totp-code';
const CODE_ERROR_ID = `${CODE_INPUT_ID}-error`;

/** Digits in a TOTP code (RFC 6238 default; matches `@puffin-core/authenticator-enrollment`). */
const CODE_LENGTH = 6;

/**
 * The server's refusals of a code (`createAuthenticatorChallengeRouter`, palm-api's limiter) → the
 * catalog leaf that words each. A refused code never reads the server's own sentence.
 */
const REFUSAL_LEAF = new Map([
  ['TOTP_INCORRECT', 'authenticator.error.code'],
  ['CHALLENGE_ALREADY_RESOLVED', 'authenticator.error.incomplete'],
  ['CHALLENGE_NOT_FOUND', 'authenticator.error.incomplete'],
  // The challenge was consumed but no session minted: retrying the code cannot work.
  ['SIGN_IN_NOT_ISSUED', 'authenticator.error.incomplete'],
  ['RATE_LIMIT_EXCEEDED', 'authenticator.error.rateLimited'],
]);

/** The submit's own failures (`useAuthenticatorConfig.submitTotpCode`) → their leaves. */
const SUBMIT_FAILURE_LEAF = new Map([
  ['NETWORK_ERROR', 'authenticator.error.network'],
  ['SESSION_INCOMPLETE', 'authenticator.error.incomplete'],
]);

/**
 * The catalog leaf a failed code submit reads: the server's refusal by its CODE, else the submit's
 * own failure by its token, else "the code could not be checked". Never the failure's message — the
 * server's English (OWNER-RULINGS-WALK-BUILDS N138, `web/no-thrown-message-as-copy`).
 *
 * @param {*} failure - the rejection
 * @returns {string}
 */
function failureLeaf(failure) {
  return (
    REFUSAL_LEAF.get(failure?.errorCode) ??
    SUBMIT_FAILURE_LEAF.get(failure?.code) ??
    'authenticator.error.codeCheckFailed'
  );
}

/**
 * @param {Object} props
 * @param {string} props.challengeId
 * @param {Function} props.submitTotpCode - `async (challengeId, code) => payload`.
 * @param {Function} [props.t]
 * @returns {React.JSX.Element}
 */
export function AuthenticatorCodeEntry({ challengeId, submitTotpCode, t }) {
  const [code, setCode] = useState('');
  // 🔴 ONE state area, not two. `error` and `submitting` always change together — a submit clears
  // the error and sets the flag; a settle clears the flag and may set an error — so splitting them
  // was two names for one transition, and it put this component over
  // `web/component-responsibility-check`'s ceiling. GEN0-STRUCTURAL-ELIMINATION says decompose or
  // simplify, never suppress.
  // `leaf` (the catalog key the failure reads) is set exactly when the attempt failed, so a failure
  // can never render NOTHING — the defect the old separate `failed` flag existed to stop, when an
  // Error with an EMPTY message was shown as its empty message. Still ONE state area.
  const [attempt, setAttempt] = useState({ leaf: null, submitting: false });

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setAttempt({ leaf: null, submitting: true });
      try {
        await submitTotpCode(challengeId, code);
      } catch (submitError) {
        // 🔴 A wrong code does NOT consume the challenge server-side, so the honest behaviour is to
        // let the user try again rather than ending the sign-in. The rate limiter bounds guessing.
        setAttempt({ leaf: failureLeaf(submitError), submitting: false });
        return;
      }
      setAttempt({ leaf: null, submitting: false });
    },
    [challengeId, code, submitTotpCode],
  );

  const translate = useLoginTranslate(t);
  const errorText = attempt.leaf ? translate(attempt.leaf) : null;

  return (
    <form onSubmit={handleSubmit} data-testid="authenticator-code-form">
      {/* D12b: the ONE code field. A sign-in challenge, so the sixth digit submits (through this
          form), as <MfaVerification> does; a refused code stays and is never auto-submitted again.
          The input stays enabled while a code is checked, as it was. The refusal is the FIELD's
          error line — linked to the input and marking it invalid; the visible prompt is the label. */}
      <MfaCodeInput
        id={CODE_INPUT_ID}
        data-testid="authenticator-code-input"
        code={code}
        onChange={setCode}
        label={translate('authenticator.codePrompt')}
        autoSubmit
        isLoading={attempt.submitting}
        disabled={false}
        error={errorText}
        errorId={CODE_ERROR_ID}
      />
      {errorText ? (
        <span
          id={CODE_ERROR_ID}
          className="form-error"
          role="alert"
          data-testid="authenticator-code-error"
        >
          {errorText}
        </span>
      ) : null}
      <button
        type="submit"
        className="btn btn-primary"
        disabled={code.length !== CODE_LENGTH || attempt.submitting}
        data-testid="authenticator-code-submit"
      >
        {translate('authenticator.submitCode')}
      </button>
    </form>
  );
}

AuthenticatorCodeEntry.propTypes = {
  challengeId: PropTypes.string.isRequired,
  submitTotpCode: PropTypes.func.isRequired,
  t: PropTypes.func,
};
